import type { DelegationView } from '@nest-recruit/shared';
import { ArrowLeftRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { Card } from '../../../../components/Card';
import { toneClass } from '../../../../components/Tone';
import { formatDate, formatIsoDate } from '../../../../lib/format';
import { COVER_STATUS_LABEL, coverStatus } from '../../cover-dates';
import { coverTone } from './cover-tone';
import { fractionOf, timelineRows, timelineWindow, type PlacedBar } from './cover-timeline';
import styles from './CoverTimeline.module.css';

interface CoverTimelineProps {
  readonly covers: readonly DelegationView[];
  readonly now: number;
  /** Today in Dhaka, `YYYY-MM-DD`. */
  readonly today: string;
}

/** "Who is away, and who covers": five weeks with a red line for today (Option A). The list under it keeps every detail. */
export function CoverTimeline({ covers, now, today }: CoverTimelineProps): ReactNode {
  const [shiftWeeks, setShiftWeeks] = useState(0);
  const span = timelineWindow(today, shiftWeeks);
  const rows = timelineRows(covers, span);
  const todayAt = fractionOf(span, now);

  return (
    <Card
      title="Who is away, and who covers"
      action={
        <div className={styles.nav}>
          <Button variant="ghost" size="sm" icon={ChevronLeft} aria-label="A week earlier" onClick={() => setShiftWeeks((weeks) => weeks - 1)} />
          <Button variant="quiet" size="sm" disabled={shiftWeeks === 0} onClick={() => setShiftWeeks(0)}>
            Today
          </Button>
          <Button variant="ghost" size="sm" icon={ChevronRight} aria-label="A week later" onClick={() => setShiftWeeks((weeks) => weeks + 1)} />
        </div>
      }
    >
      <div className={styles.grid} data-testid="cover-timeline">
        <div className={styles.head}>
          <span className={styles.awayHead}>Away</span>
          {span.weeks.map((week) => (
            <span key={week} className={styles.week}>
              Week of <span className={styles.weekDate}>{formatIsoDate(week)}</span>
            </span>
          ))}
        </div>

        <div
          className={`${styles.body} ${todayAt === null ? '' : styles.hasToday}`}
          style={todayAt === null ? undefined : ({ '--today-at': todayAt } as CSSProperties)}
        >
          {rows.length === 0 ? (
            <p className={styles.nobody}>Nobody is away in these five weeks.</p>
          ) : (
            rows.map((row) => (
              <div key={row.fromUserId} className={styles.row}>
                <span className={styles.who}>
                  <b className={styles.name}>{row.fromUserName}</b>
                  <span className={styles.status}>{row.bars[0] ? COVER_STATUS_LABEL[coverStatus(row.bars[0].cover, now)] : '—'}</span>
                </span>
                <span className={styles.track}>
                  {row.bars.map(({ cover, bar }) => (
                    <Bar key={cover.id} cover={cover} bar={bar} now={now} />
                  ))}
                </span>
              </div>
            ))
          )}

          {todayAt !== null && (
            <>
              <span className={styles.todayLine} data-testid="today-line" aria-hidden />
              <span className={styles.todayLabel} aria-hidden>
                Today
              </span>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

function Bar({ cover, bar, now }: { readonly cover: DelegationView; readonly bar: PlacedBar; readonly now: number }): ReactNode {
  const label = (
    <>
      <ArrowLeftRight size={12} aria-hidden />
      {cover.toUserName} covers
    </>
  );
  const what = `${cover.toUserName} covers ${cover.fromUserName}, ${formatDate(cover.startsAt)} to ${formatDate(cover.endsAt)}`;
  const outside: CSSProperties =
    bar.label === 'after'
      ? { insetInlineStart: `calc(${bar.left + bar.width}% + var(--flock-space-2))`, maxInlineSize: `calc(${bar.labelRoom}% - var(--flock-space-2))` }
      : { insetInlineEnd: `calc(${100 - bar.left}% + var(--flock-space-2))`, maxInlineSize: `calc(${bar.labelRoom}% - var(--flock-space-2))` };

  return (
    <>
      <span
        className={`${toneClass} ${styles.bar}`}
        data-tone={coverTone(coverStatus(cover, now))}
        data-label={bar.label}
        style={{ insetInlineStart: `${bar.left}%`, inlineSize: `${bar.width}%` }}
        title={what}
      >
        {bar.label === 'inside' && <span className={styles.barText}>{label}</span>}
      </span>
      {bar.label !== 'inside' && (
        <span className={styles.outside} data-label={bar.label} style={outside} title={what}>
          {label}
        </span>
      )}
    </>
  );
}
